/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Top-view boat icons from the Deckee boat sprite sheet (`sprite@2x.png`).
 * Names follow `boat-{type}-top`. The tilted `boat-{type}-{00..15}` frames
 * are in the sheet but are not registered yet.
 */

export type BoatSpriteType =
  | 'motorboat'
  | 'sailboat'
  | 'pwc'
  | 'paddle'
  | 'superyacht'
  | 'trawler'
  | 'pilot'
  | 'sar'
  | 'police'
  | 'tug'
  | 'warship'
  | 'ferry'
  | 'passenger'
  | 'cruise'
  | 'ship';

export const boatTopIconName = (type: BoatSpriteType): string =>
  `boat-${type}-top`;

export const DEFAULT_BOAT_TOP_ICON = boatTopIconName('motorboat');

// Icon names on AIS boat payloads, from containers/ais ais-types.js
const AIS_ICON_SPRITE_TYPES: { [aisIcon: string]: BoatSpriteType } = {
  vesselIconFishingBoat: 'trawler',
  vesselIconTugBoat: 'tug',
  vesselIconMilitaryShip: 'warship',
  vesselIconSailBoat: 'sailboat',
  vesselIconSpeedBoat: 'motorboat',
  vesselIconPilotBoat: 'pilot',
  vesselIconRescueBoat: 'sar',
  vesselIconPoliceBoat: 'police',
  vesselIconFerry: 'ferry',
  vesselIconCargoShip: 'ship',
  // No tanker hull in the sheet
  vesselIconTanker: 'ship',
  vesselIconMotorBoat: 'motorboat',
  // Not assigned by the AIS type table today
  vesselIconSmallBoat: 'motorboat',
  vesselIconSuperyacht: 'superyacht',
  vesselIconCruiseShip: 'cruise',
};

export const boatIconForAisIcon = (aisIcon?: string | null): string => {
  const type = aisIcon ? AIS_ICON_SPRITE_TYPES[aisIcon] : undefined;
  return type ? boatTopIconName(type) : DEFAULT_BOAT_TOP_ICON;
};

const WATERCRAFT_SPRITE_TYPES: { [watercraftType: string]: BoatSpriteType } = {
  motorboat: 'motorboat',
  sailboat: 'sailboat',
  'personal-watercraft': 'pwc',
  'paddle-craft': 'paddle',
};

/** Deckee watercraft type (VesselTypeEnum) to a top-view icon. Unknown is the motorboat. */
export const boatIconForWatercraftType = (
  watercraftType?: string | null,
): string => {
  // Older records use spaces ("personal watercraft") instead of hyphens
  const normalized = (watercraftType || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');
  const type = WATERCRAFT_SPRITE_TYPES[normalized];
  return type ? boatTopIconName(type) : DEFAULT_BOAT_TOP_ICON;
};

/**
 * Tilted (3D) frame with the bow pointing left (frame 12, 270° to the
 * camera), e.g. for a popover when the map is pitched.
 */
export const leftFacingBoatIcon = (topIconName: string): string =>
  topIconName.replace(/-top$/, '-12');

/** Boats with no known heading point left (west). */
export const DEFAULT_BOAT_HEADING = 270;

/**
 * Heading in degrees for `icon-rotate`. Missing, non-numeric, and the GPS
 * "invalid" value (-1) all face left (`DEFAULT_BOAT_HEADING`).
 */
export const normalizeHeading = (heading: unknown): number => {
  const value = typeof heading === 'string' ? Number(heading) : heading;
  if (typeof value !== 'number' || !isFinite(value) || value < 0) {
    return DEFAULT_BOAT_HEADING;
  }
  return value % 360;
};

// Sprite cells are 128 x 174 at pixelRatio 2, so 64 px wide at icon-size 1.
// The boat fills about 70% of the width: ~24 px at zoom 8.5, ~36 px at 14.
// (The README ramp, reduced by 10%.)
const BOAT_ICON_SIZE_STOPS: [number, number][] = [
  [8.5, 0.54],
  [12, 0.675],
  [14, 0.81],
  [16, 1.035],
  [18, 1.35],
  [20, 1.71],
];

const SELECTED_BOAT_ICON_SCALE = 1.25;

const isSelected = ['==', ['get', 'markerSelected'], true];

/** Smooth zoom ramp for boat icons. Selected markers sit slightly larger on the same curve. */
export const boatIconSize: any[] = BOAT_ICON_SIZE_STOPS.reduce<any[]>(
  (expression, [zoom, size]) =>
    expression.concat([
      zoom,
      ['case', isSelected, size * SELECTED_BOAT_ICON_SCALE, size],
    ]),
  ['interpolate', ['linear'], ['zoom']],
);

/**
 * `boatIconSize`, hidden below `minZoom` unless the marker is selected. For
 * layers that keep a selected marker on the map when zoomed out.
 */
export const boatIconSizeShownFrom = (minZoom: number): any[] => {
  const [, firstSize] = BOAT_ICON_SIZE_STOPS[0];
  const selectedSize = firstSize * SELECTED_BOAT_ICON_SCALE;
  return [
    ...boatIconSize.slice(0, 3),
    minZoom - 0.0001,
    ['case', isSelected, selectedSize, 0],
    minZoom,
    ['case', isSelected, selectedSize, firstSize],
    ...boatIconSize.slice(3),
  ];
};

/** Shared symbol layout for top-view boats. Feature properties need `image` and `heading`. */
export const boatIconLayout = {
  'icon-image': ['to-string', ['get', 'image']],
  'icon-size': boatIconSize,
  // The waterline centre is the vertical centre of the (tall) cell
  'icon-anchor': 'center',
  'icon-rotate': ['to-number', ['get', 'heading'], DEFAULT_BOAT_HEADING],
  'icon-rotation-alignment': 'map',
  'icon-allow-overlap': true,
  'text-allow-overlap': true,
};

/** Hide boat icons below this zoom with `minzoom`, rather than shrinking them to 0. */
export const BOAT_ICON_MIN_ZOOM = 8.5;
